// One player, in one league: the card that opens from any player name
// (SCENARIOS E2, BACKLOG E4). `GET /leagues/:id/players/:playerId`, served
// since W7 of PLAN-2026-09-24-free-agents-and-waivers (API.md §3).
//
// THE LEAGUE'S HALF OF THE CARD. Who holds the player here and what I can do
// about it, and his season scored this league's way. What the player is
// regardless of league — the injury report, the fixture, the weather — is the
// Info Manager's dossier (`PlayerDetailView`), whose serving is an open
// decision (`context.ts`); a card that grows it gains a field, additively.
//
// TWO "AVAILABILITY"s, KEPT APART BY NAME. `Availability` (views.ts) is
// ownership in a league: free agent, on waivers, somebody's, mine.
// `AvailabilityView` (player-detail.ts) is whether he can score this period at
// all. A card that carried both under one word would be read wrong by whoever
// had only met the other, so the league's one is `inLeague` here.

import { z } from 'zod';
import {
  Availability,
  DeeperNumbers,
  GameStatus,
  PlayerAction,
  PlayerSummary,
  ProjectionBasis,
} from './views';
import { LastSeasonView, PlayerWeekRow, PlayerWeeksView } from './player-weeks';
import { StatsFreshness } from './clock';

/** A page on another site about the player, a plain link out (B-482). */
export const PlayerPageElsewhere = z.object({
  /** The site, as a person names it: "ESPN". */
  site: z.string(),
  /** The page's address. */
  url: z
    .string()
    .url()
    .refine((u) => u.startsWith('https://'), 'A page elsewhere is an https address.'),
});
export type PlayerPageElsewhere = z.infer<typeof PlayerPageElsewhere>;

export const PlayerCardView = z.object({
  leagueId: z.string(),
  player: PlayerSummary,
  /**
   * Pages on other sites that keep his game-day status current, which our
   * news, the league office's injury report, does not (DIRECTION D10): a
   * client links out to each in a new tab and says whose site it is (B-482).
   * Empty for a player with none, and in a world whose players have no
   * vendor keys (the seeder's). Added 2026-10-23.
   */
  elsewhere: z.array(PlayerPageElsewhere).default([]),
  /** His pro game this period, from the league's calendar; null when there is none. */
  game: GameStatus.nullable(),
  /** This period, in the league's scoring. Null when nobody projected him (`PlayerRowView`). */
  projection: z.number().nullable(),
  /** Where the projection came from, the row's shape (`PlayerRowView.basis`). Added 2026-09-25 (N3). */
  basis: ProjectionBasis.nullable(),
  /** Who holds him in this league, with his clear time when he is on waivers. */
  inLeague: Availability,
  /**
   * His game this period has started: nobody drops him until the period ends,
   * and as a free agent he is claimed rather than added (PLAN-2026-09-24 §2
   * rule 6), which `inLeague` and `action` already say.
   */
  locked: z.boolean(),
  /** What I can do: the same vocabulary as a row in the pool. */
  action: PlayerAction,
  /** Why `action` is `none`, when it is. */
  reason: z.string().nullable(),
  /** On my watchlist (`WatchlistView`, E5). Added 2026-10-03. */
  watched: z.boolean().default(false),
  /**
   * His rank at his first position by season points in this league's
   * scoring, the Players row's (`PlayerRowView.seasonRank`). Added
   * 2026-10-14 (B-321).
   */
  seasonRank: z.number().int().min(1).nullable().default(null),
  /** The deeper numbers (E8), the Players row's, for the Everything level. Added 2026-10-03. */
  deeper: DeeperNumbers.nullable().default(null),
  /**
   * The share of the site's leagues that roster him, which way he is moving
   * and his net adds over seven days: the Players row's `ownershipPct`,
   * `trend` and `adds`, null where they are. Added 2026-10-18 (B-414).
   */
  ownershipPct: z.number().nullable().default(null),
  trend: z.enum(['up', 'down', 'flat']).nullable().default(null),
  adds: z.number().int().nullable().default(null),
  /**
   * His season before this league's, in its scoring now (`LastSeasonView`);
   * null with no game in it. Added 2026-10-18 (B-414).
   */
  lastSeason: LastSeasonView.nullable().default(null),
  /**
   * His season so far, scored this league's way: exactly what
   * `GET …/players/:playerId/weeks` answers, less what the card already says
   * (`heldBy` is `inLeague`). Null for a league with no calendar yet, where
   * that read answers 409.
   */
  seasonSoFar: PlayerWeeksView.pick({
    season: true,
    firstPeriod: true,
    summary: true,
    weeks: true,
    columns: true,
    upcoming: true,
  }).nullable(),
  /**
   * This period — the one `game` and `projection` are for — as
   * `seasonSoFar` reads it: his points and line once they are in, and
   * otherwise which kind of nothing (`no-line` once his game's lines are in
   * and he has none, `pending` while they are not). Null before the period
   * has started, and with no calendar. The card's own "this week", so it does
   * not depend on what the screen that opened it knew. Added 2026-09-25
   * (round 3's review, SCREENS-3).
   */
  thisWeek: PlayerWeekRow.nullable(),
  /**
   * How fresh this period's stat lines are — the scoreboard's `stats` for
   * the same period (BACKLOG L2). Added 2026-09-25.
   */
  stats: StatsFreshness,
  asOf: z.string(),
});
export type PlayerCardView = z.infer<typeof PlayerCardView>;
